import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { AlertTriangle } from "lucide-react";

interface RemoveLabPartnerConfirmModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void;
  loading?: boolean;
}

const RemoveLabPartnerConfirmModal: React.FC<RemoveLabPartnerConfirmModalProps> = ({
  open,
  setOpen,
  onConfirm,
  loading = false,
}) => {
  const cancelButtonRef = useRef(null);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={() => setOpen(false)}
      >
        <div className="flex items-end justify-center min-h-screen px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-xl overflow-hidden text-left shadow-2xl transform transition-all sm:my-8 sm:align-middle w-full sm:max-w-md">
              <div className="bg-[#F9FAFB] px-4 py-4 flex items-center justify-between border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-red-100 rounded-xl">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">
                      Remove Lab Partner
                    </h2>
                  </div>
                </div>

                <button
                  type="button"
                  className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition"
                  onClick={() => setOpen(false)}
                >
                  <XMarkIcon className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <div className="p-6">
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  Are you sure you want to remove the lab partner from mapping? 
                </p>
                <span className="text-xs text-gray-500">
                    This action cannot be undone.
                  </span>
              </div>

              <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                <button
                  ref={cancelButtonRef}
                  type="button"
                  className="flex-1 px-4 py-2.5 text-xs font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={loading}
                  onClick={onConfirm}
                  className={clsx(
                    "flex-1 px-4 py-2.5 text-xs font-bold rounded-xl transition-all",
                    {
                      "bg-red-600 text-white hover:bg-red-700": !loading,
                      "bg-gray-200 text-gray-400 cursor-not-allowed": loading,
                    }
                  )}
                >
                  {loading ? "Removing..." : "Remove Lab Partner"}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default RemoveLabPartnerConfirmModal;
